import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import CanvasContainer from 'benchling-apps/interactivity/canvases/CanvasContainer';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as Context from 'Context';
import {Tag} from 'folder-items/FileConstants';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingAppFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  InstrumentServiceDetailsLoader,
  InstrumentServiceLoadStatus,
} from 'instruments/connections/ConnectionConstants';
import {getInstrumentServiceDetails} from 'instruments/connections/ConnectionDAO';
import RunAutomationFileViewer from 'lab-automation/automation-files/RunAutomationFileViewer';
import {
  FromServerRunOutputSamplesTable,
  RunOutputSamplesTable,
} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {FromServerResultsTable, ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {NOTE_PADDING} from 'notebook/notes/NoteConstants';
import {Review} from 'notebook/reviews/ReviewCriteriaConstants';
import notifications from 'notifications';
import {BenchlingDispatch, getStore} from 'redux-stores/Store';
import {
  GetAnalysisTemplateForAssayRun,
  GetAnalysisTemplateForAssayRunVariables,
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayRecordOriginType} from 'results/assays/AssayRecordConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {EditRunToolbar, SubmitToolbar, validateRunForSubmit} from 'runs/assay-group/AssayGroupEditButtons';
import {updateTagsForAssayRun} from 'runs/assay-group/AssayGroupReduxActions';
import AssayResultsContainer, {AssayResultsContainerProps} from 'runs/assay-group/AssayResultsContainer';
import AssayRunFields from 'runs/assay-group/AssayRunFields';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import CollapserChevron from 'ui-library/CollapserChevron';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {NLADVersionedAutomationFiles} from '../AttachmentConstants';
import AssayGroupContainer from './AssayGroupContainer';
import {computeVisibleRunFields, getEditableRunTags} from './VisibleRunFields';

const isExpandedGlobalClientState = makeUseGlobalClientState<boolean>(true);

interface AssayGroupHeaderProps {
  editButtons?: React.ReactNode;
  titleText: string;
  originType?: string;
}

export function AssayGroupHeader(props: AssayGroupHeaderProps): JSX.Element {
  return (
    <div className="u-lr-split u-spacing-bottom">
      <div>
        <span
          className="resultsAttachment-assayRun-titleText"
          {...getTestProps({component: 'AssayGroupHeader', element: 'titleText'})}
        >
          {props.titleText}
        </span>
        {props.originType === AssayRecordOriginType.API && (
          <span className="resultsAttachment-assayRun-originType u-small-spacing-left">API</span>
        )}
      </div>
      <div className="resultsAttachment-assayRun-statusText">{props.editButtons}</div>
    </div>
  );
}

export interface AssayGroupProps extends AssayResultsContainerProps {
  assayRunSchemaInfo: {
    assayRunSchema: AssayRunSchema;
    versionedName: string | null;
    versionedFields: Array<SchemaField> | null;
  };
  assayRunInfo: {
    assayRunId: string | null;
    versionedTags: Array<Tag> | null;
    versionedConnectionId: string | null;
    versionedEquipmentId: string | null;
  };
  automationFiles: NLADVersionedAutomationFiles;
  versionedStructuredTables: Array<RunOutputSamplesTable | ResultsTable>;
  handleRefreshVersionedData: () => Promise<void>;
  handleUpdateTags: (tags: Array<Tag>, connectionId: string | null, equipmentId: string | null) => void;
  handleUpdateAutomationFiles: (
    files: Partial<NLADVersionedAutomationFiles>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
  handleRemoveAssayRun: () => void;
  resultSchemaToCount: {[schemaId: string]: number};
  review: Review | null;
  refreshCounter?: number;
}

interface DispatchProps {
  onUpdateAssayRun: (
    assayRunId: string,
    cid: string,
    tags: Array<Tag>,
    connectionId: string | null,
    equipmentId: string | null,
    studyIds: Array<string>
  ) => Promise<void>;
}

// exported for tests
export function shouldDisableEditButton(
  hasEditableRunTags: boolean,
  hasConnectionSchema: boolean,
  hasEditableEquipment: boolean,
  generatedInputFiles: boolean,
  processedOutputFiles: boolean
): boolean {
  const isConnectionEditable =
    isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_EDIT}) &&
    hasConnectionSchema &&
    !generatedInputFiles &&
    !processedOutputFiles;
  const isEquipmentEditable = hasEditableEquipment && !generatedInputFiles && !processedOutputFiles;
  const areFieldsEditable = hasEditableRunTags || isConnectionEditable || isEquipmentEditable;
  return (
    !areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_EDIT}]) ||
    !areFieldsEditable ||
    BenchlingConfig.LAB_AUTOMATION_LOCK_RUN_AFTER_CREATION ||
    (generatedInputFiles && BenchlingConfig.LAB_AUTOMATION_LOCK_RUN_AFTER_INPUT_FILE_GENERATED) ||
    (processedOutputFiles && BenchlingConfig.LAB_AUTOMATION_LOCK_RUN_AFTER_OUTPUT_FILE_PROCESSED)
  );
}

const AssayGroup: React.FunctionComponent<AssayGroupProps & DispatchProps> = (props) => {
  const [isEditing, setEditing] = useState(false);
  const [isExpanded, setIsExpanded] = isExpandedGlobalClientState.useGlobalClientState(props.cid);

  const response = useSkippableBenchlingQuery<
    GetAnalysisTemplateForAssayRun,
    GetAnalysisTemplateForAssayRunVariables
  >(GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY, {
    variables: {analysisTemplateId: props.assayRunSchemaInfo.assayRunSchema.analysisTemplateId!},
    skip: props.assayRunSchemaInfo.assayRunSchema.analysisTemplateId == null,
  });

  let analysisTemplate: AnalysisTemplateNode | null = null;
  if (
    response &&
    response.type === QueryResultType.SUCCESS &&
    !isInaccessible(response.data.analysisTemplate)
  ) {
    analysisTemplate = response.data.analysisTemplate;
  }

  const visibleRunFields = computeVisibleRunFields(
    props.assayRunSchemaInfo.assayRunSchema,
    props.displayConfigBySchemaId[props.assayRunSchemaInfo.assayRunSchema.id]
  );
  const locked =
    props.locked || !areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_EDIT}]);
  const hasEditableRunTags =
    getEditableRunTags(visibleRunFields, props.assayRun, props.assayRunInfo.versionedTags).length > 0;
  const generatedInputFiles =
    props.automationFiles.automationInputFiles != null &&
    props.automationFiles.automationInputFiles.filter(
      (aif) => aif.status === AutomationFileStatusGraphQL.succeeded
    ).length > 0;
  const processedOutputFiles =
    props.automationFiles.automationOutputFiles != null &&
    props.automationFiles.automationOutputFiles.filter(
      (aof) => aof.status === AutomationFileStatusGraphQL.succeeded
    ).length > 0;
  const connectionSchema = props.assayRunSchemaInfo.assayRunSchema.connectionSchema;
  const disableEditButton = shouldDisableEditButton(
    hasEditableRunTags,
    connectionSchema != null,
    props.assayRun.shouldRecordEquipment,
    generatedInputFiles,
    processedOutputFiles
  );
  const isAutomationRun =
    props.assayRunSchemaInfo.assayRunSchema.automationInputFileConfigs.length ||
    props.assayRunSchemaInfo.assayRunSchema.automationOutputFileConfigs.length;

  const connectionId = props.assayRunInfo.versionedConnectionId ?? props.assayRun?.connection?.id ?? null;

  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState<InstrumentServiceDetailsLoader>({
    status: InstrumentServiceLoadStatus.UNSET,
    data: null,
  });

  useEffect((): void => {
    if (connectionId) {
      setInstrumentServiceDetails({
        status: InstrumentServiceLoadStatus.LOADING,
        data: null,
      });
      getInstrumentServiceDetails(connectionId)
        .then((data) => {
          setInstrumentServiceDetails({data, status: InstrumentServiceLoadStatus.AVAILABLE});
        })
        .catch((error) => {
          notifications.notifyError(error);
          setInstrumentServiceDetails({data: null, status: InstrumentServiceLoadStatus.UNAVAILABLE});
        });
    }
  }, [connectionId]);
  const connectionStatus = instrumentServiceDetails.data?.status.value;

  return (
    <>
      <AssayGroupContainer
        indentation={props.indentation}
        title={
          <AssayGroupHeader
            editButtons={
              isEditing ? (
                <SubmitToolbar
                  onCancel={() => {
                    setEditing(false);
                  }}
                  validateForSubmit={() =>
                    validateRunForSubmit(
                      getStore().getState().ui.schemaTags.tagsByCid[props.cid],
                      getStore().getState().ui.schemaTags.connectionIdByCid[props.cid] ?? null,
                      connectionSchema
                    )
                  }
                  onSubmit={async () => {
                    const tags = getStore().getState().ui.schemaTags.tagsByCid[props.cid];
                    const connectionId =
                      getStore().getState().ui.schemaTags.connectionIdByCid[props.cid] ?? null;
                    const equipmentId =
                      getStore().getState().ui.schemaTags.equipmentIdByCid[props.cid] ?? null;
                    const studyIds = getStore().getState().ui.schemaTags.studyIdsByCid[props.cid] ?? [];
                    props.handleUpdateTags(tags, connectionId, equipmentId);
                    setEditing(false);
                    await Context.usingAsync(
                      {
                        analyticsMetadata: {
                          actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_RUN,
                        },
                      },
                      null,
                      async () => {
                        await props.onUpdateAssayRun(
                          props.assayRun.id,
                          props.cid,
                          tags,
                          connectionId,
                          equipmentId,
                          studyIds
                        );
                      }
                    );
                  }}
                  submitText="Save"
                />
              ) : (
                <EditRunToolbar
                  assayResults={props.assayResults}
                  assayRun={props.assayRun}
                  assayRunSchema={props.assayRunSchemaInfo.assayRunSchema}
                  childrenFetching={props.childrenFetching}
                  handleRefreshVersionedData={props.handleRefreshVersionedData}
                  handleRemoveAssayRun={props.handleRemoveAssayRun}
                  handleUpdateAutomationFiles={props.handleUpdateAutomationFiles}
                  onClickEdit={() => {
                    setEditing(true);
                  }}
                  locked={locked}
                  disableEditButton={disableEditButton}
                  review={props.review}
                  automationFiles={props.automationFiles}
                />
              )
            }
            originType={props.assayRun.originType}
            titleText={props.assayRunSchemaInfo.versionedName || props.assayRunSchemaInfo.assayRunSchema.name}
          />
        }
        assayRunIdForValidationFetch={props.assayRun.id}
        showValidationSideBar={false}
      >
        {isExpanded ? (
          <>
            <AssayRunFields
              assayRun={props.assayRun}
              analysisTemplate={analysisTemplate ?? undefined}
              liveFields={visibleRunFields}
              versionedFields={props.assayRunSchemaInfo.versionedFields}
              versionedTags={props.assayRunInfo.versionedTags}
              cid={props.cid}
              isEditable={isEditing}
              connectionSchemaId={connectionSchema?.id ?? null}
              versionedConnectionId={props.assayRunInfo.versionedConnectionId}
              versionedEquipmentId={props.assayRunInfo.versionedEquipmentId}
              connectionStatus={connectionStatus}
              hasAutomationFiles={generatedInputFiles || processedOutputFiles}
            />
            <CanvasContainer
              resourceId={props.assayRun.id}
              featureType={BenchlingAppFeatureType.ASSAY_RUN}
              enabledOverride={!locked}
            />
            <RunAutomationFileViewer
              runId={props.assayRun.id}
              automationFiles={props.automationFiles}
              locked={locked || isEditing}
              isConnectionRun={connectionSchema != null}
              connectionId={props.assayRun.connection?.id ?? null}
              instrumentServiceDetails={instrumentServiceDetails}
              handleRefreshVersionedData={props.handleRefreshVersionedData}
              handleUpdateAutomationFiles={props.handleUpdateAutomationFiles}
              versionedStructuredTables={props.versionedStructuredTables}
              getIdFromCid={props.editorProps.getIdFromCid}
              fileId={props.fileId}
              viewer={props.viewer}
              refreshCounter={props.refreshCounter}
            />
            {!isAutomationRun && (
              <AssayResultsContainer
                assayResults={props.assayResults}
                assayResultSchemas={props.assayResultSchemas}
                childrenFetching={props.childrenFetching}
                dataTables={props.dataTables}
                displayConfigBySchemaId={props.displayConfigBySchemaId}
                editor={props.editor}
                editorProps={props.editorProps}
                handleUpdateNoteLinkedAssayData={props.handleUpdateNoteLinkedAssayData}
                assayRun={props.assayRun}
                provenanceUuid={props.provenanceUuid}
                resultSchemaToCount={props.resultSchemaToCount}
                usedTableNames={props.usedTableNames}
                /* Proxied note props */
                cid={props.cid}
                fileId={props.fileId}
                indentation={props.indentation}
                index={props.index}
                locked={locked}
                viewer={props.viewer}
              />
            )}
          </>
        ) : null}
      </AssayGroupContainer>
      <div className={css(styles.collapserChevron)}>
        <CollapserChevron
          isExpanded={isExpanded}
          name={MODEL_NAMES.run}
          onToggleCollapsed={() => {
            setIsExpanded(!isExpanded);
          }}
        />
      </div>
    </>
  );
};

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onUpdateAssayRun: (
      assayRunId: string,
      cid: string,
      tags: Array<Tag>,
      connectionId: string | null,
      equipmentId: string | null,
      studyIds: Array<string>
    ) => dispatch(updateTagsForAssayRun(assayRunId, cid, tags, connectionId, equipmentId, studyIds)),
  };
}

export default connect(null, mapDispatchToProps)(AssayGroup);

const styles = StyleSheet.create({
  collapserChevron: {paddingTop: NOTE_PADDING, marginLeft: -5},
});
